/**
 * MtgjsonLoadStatus component
 * Displays real-time status of MTGJSON background load process (3-5 hour operation)
 * 
 * Shows progress for:
 * - Sets data (AllPrintings.json → sets table)
 * - Supplementary data (AllIdentifiers, CardTypes, Keywords, SetList)
 * - Pricing data (AllPrices, AllPricesToday)
 * - Deck data (DeckList, AllDeckFiles)
 */

import React, { useEffect, useState } from 'react';
import { fetchLoadStatus, type LoadStatus } from '../../api/mtgjson';
import { colors, spacing, typographyFlat } from '../../styles/tokens';
import { formatInstant, parseInstant } from '../../utils/datetime';

export const MtgjsonLoadStatus: React.FC = () => {
  const [status, setStatus] = useState<LoadStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [lastFetchMs, setLastFetchMs] = useState<number>(Date.now());
  const [uiNowMs, setUiNowMs] = useState<number>(Date.now());
  const isInitialLoad = React.useRef(true);

  useEffect(() => {
    const tick = setInterval(() => {
      setUiNowMs(Date.now());
    }, 1000);

    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        // Only show loading state on initial load, not on refresh
        if (isInitialLoad.current) {
          setLoading(true);
        }
        const loadData = await fetchLoadStatus();
        setStatus(loadData);
        setLastFetchMs(Date.now());
        setError(null);
      } catch (err) {
        console.error('Failed to load MTGJSON load status:', err);
        setError('Failed to load status');
      } finally {
        if (isInitialLoad.current) {
          setLoading(false);
          isInitialLoad.current = false;
        }
      }
    };

    loadStatus();
    // Poll backend regularly for fresh data while UI timers update every second locally.
    const interval = setInterval(() => {
      loadStatus();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatETA = (seconds: number | null): string => {
    if (seconds === null) return 'Calculating...';
    return formatDuration(seconds);
  };

  const formatActivityUpdatedAt = (value?: string | null): string =>
    formatInstant(value, 'Unknown');

  const formatHeartbeatAge = (value?: string | null): string => {
    if (!value) return 'Unknown';
    const d = parseInstant(value);
    if (!d) return 'Unknown';
    const diffSeconds = Math.max(0, Math.floor((uiNowMs - d.getTime()) / 1000));
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    return `${diffHours}h ago`;
  };

  const getStatusColor = (stepStatus: string): string => {
    switch (stepStatus) {
      case 'success':
        return colors.success;
      case 'failed':
        return colors.error;
      case 'warning':
        return colors.warning;
      default:
        return colors.textSecondary;
    }
  };

  const getStepPhaseLabel = (step: LoadStatus['steps'][string]): string => {
    if (step.phase === 'timed_out') {
      return 'Timed out';
    }
    if (step.phase === 'post_import_pending_validation') {
      return 'Awaiting validation';
    }
    if (step.status === 'success') {
      return '✓ Complete';
    }
    if (step.phase === 'importing' || step.phase === 'downloading') {
      return `🔄 ${step.phase}`;
    }
    return step.phase;
  };

  const getOverallStatusColor = (overallStatus: string): string => {
    switch (overallStatus) {
      case 'completed':
        return colors.success;
      case 'loading':
        return colors.primary;
      case 'failed':
        return colors.error;
      default:
        return colors.textSecondary;
    }
  };

  const getStatusLabel = (overallStatus: string): string => {
    switch (overallStatus) {
      case 'not_started':
        return 'Not Started';
      case 'loading':
        return 'Loading...';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return 'Unknown';
    }
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.md,
      padding: spacing.lg,
      backgroundColor: colors.backgroundAlt,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      userSelect: 'none',
    },
    title: {
      fontSize: typographyFlat.fontSize.md,
      fontWeight: 600,
      color: colors.text,
    },
    overallStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      fontSize: typographyFlat.fontSize.sm,
      fontWeight: 500,
    },
    statusIndicator: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
    },
    summary: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: spacing.lg,
    },
    progressContainer: {
      flex: 1,
    },
    progressBar: {
      width: '100%',
      height: '24px',
      backgroundColor: colors.background,
      border: `1px solid ${colors.border}`,
      borderRadius: '4px',
      overflow: 'hidden',
      position: 'relative',
    },
    progressFill: {
      height: '100%',
      transition: 'width 0.3s ease',
    },
    progressText: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: typographyFlat.fontSize.xs,
      fontWeight: 600,
      color: colors.text,
    },
    stats: {
      display: 'flex',
      gap: spacing.lg,
      fontSize: typographyFlat.fontSize.sm,
      color: colors.textSecondary,
    },
    statItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.xs,
    },
    statLabel: {
      fontSize: typographyFlat.fontSize.xs,
      color: colors.textSecondary,
    },
    statValue: {
      fontSize: typographyFlat.fontSize.md,
      fontWeight: 600,
      color: colors.text,
    },
    stepsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.md,
    },
    step: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.sm,
      padding: spacing.md,
      backgroundColor: colors.background,
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
    },
    stepHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    stepName: {
      fontSize: typographyFlat.fontSize.sm,
      fontWeight: 600,
      color: colors.text,
      textTransform: 'capitalize',
    },
    stepStatus: {
      fontSize: typographyFlat.fontSize.xs,
      fontWeight: 500,
      padding: `${spacing.xs} ${spacing.sm}`,
      borderRadius: '4px',
    },
    stepProgressBar: {
      width: '100%',
      height: '8px',
      backgroundColor: colors.backgroundAlt,
      border: `1px solid ${colors.border}`,
      borderRadius: '4px',
      overflow: 'hidden',
    },
    stepProgressFill: {
      height: '100%',
      transition: 'width 0.3s ease',
    },
    stepDetails: {
      display: 'flex',
      gap: spacing.md,
      fontSize: typographyFlat.fontSize.xs,
      color: colors.textSecondary,
    },
    error: {
      color: colors.error,
      fontSize: typographyFlat.fontSize.sm,
    },
    loading: {
      color: colors.textSecondary,
      fontSize: typographyFlat.fontSize.sm,
    },
    runErrorBox: {
      marginTop: spacing.md,
      padding: spacing.md,
      border: `1px solid ${colors.error}`,
      borderRadius: '6px',
      backgroundColor: `${colors.error}10`,
    },
    runErrorTitle: {
      fontSize: typographyFlat.fontSize.sm,
      fontWeight: 600,
      color: colors.error,
      marginBottom: spacing.xs,
    },
    runErrorItem: {
      fontSize: typographyFlat.fontSize.xs,
      color: colors.text,
      marginBottom: spacing.xs,
      lineHeight: 1.4,
    },
    continuityBox: {
      marginTop: spacing.md,
      padding: spacing.md,
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      backgroundColor: colors.background,
    },
    continuityHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.sm,
      fontSize: typographyFlat.fontSize.sm,
      fontWeight: 600,
      color: colors.text,
    },
    continuityRow: {
      display: 'flex',
      gap: spacing.lg,
      flexWrap: 'wrap',
      fontSize: typographyFlat.fontSize.xs,
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
  };

  if (loading && !status) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading MTGJSON status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  if (!status || status.overall_status === 'not_started') {
    return (
      <div style={styles.container}>
        <div style={styles.title}>MTGJSON Background Load</div>
        <div style={styles.loading}>Not started. Load will begin after AllPrintings.psql restoration.</div>
      </div>
    );
  }

  const secondsSinceFetch = Math.max(0, Math.floor((uiNowMs - lastFetchMs) / 1000));
  const isLoading = status.overall_status === 'loading';
  const displayElapsedSeconds = isLoading
    ? (status.elapsed_seconds ?? 0) + secondsSinceFetch
    : (status.elapsed_seconds ?? 0);
  const displayEtaSeconds = status.estimated_completion === null
    ? null
    : (isLoading ? Math.max(0, status.estimated_completion - secondsSinceFetch) : status.estimated_completion);

  const stepLabels: Record<string, string> = {
    sets: 'Sets Data',
    supplementary: 'Supplementary Data',
    pricing: 'Pricing Data',
    decks: 'Deck Data',
  };

  const currentStep = status.current_step ? status.steps[status.current_step] : null;
  const currentStepOverallProgress =
    typeof status.current_step_overall_progress === 'number'
      ? status.current_step_overall_progress
      : (typeof currentStep?.overall_progress_percent === 'number'
          ? currentStep.overall_progress_percent
          : (currentStep?.progress_percent ?? null));

  let displayTotalProgress = status.total_progress;
  if (
    isLoading &&
    status.elapsed_seconds !== null &&
    status.estimated_completion !== null &&
    status.total_progress < 100
  ) {
    const totalProjectedSeconds = status.elapsed_seconds + status.estimated_completion;
    if (totalProjectedSeconds > 0) {
      const dynamicProgress = Math.round((displayElapsedSeconds / totalProjectedSeconds) * 100);
      displayTotalProgress = Math.max(status.total_progress, Math.min(99, dynamicProgress));
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.header} onClick={() => setExpanded(!expanded)}>
        <div style={styles.title}>MTGJSON Background Load</div>
        <div style={styles.overallStatus}>
          <div 
            style={{
              ...styles.statusIndicator,
              backgroundColor: getOverallStatusColor(status.overall_status),
            }}
          />
          <span>{getStatusLabel(status.overall_status)}</span>
          <span>{expanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {expanded && (
        <>
          <div style={styles.summary}>
            <div style={styles.progressContainer}>
              <div style={styles.progressBar}>
                <div 
                  style={{
                    ...styles.progressFill,
                    width: `${displayTotalProgress}%`,
                    backgroundColor: getOverallStatusColor(status.overall_status),
                  }}
                />
                <div style={styles.progressText}>{displayTotalProgress}%</div>
              </div>
            </div>

            <div style={styles.stats}>
              {status.started_at && (
                <div style={styles.statItem}>
                  <div style={styles.statLabel}>Elapsed</div>
                  <div style={styles.statValue}>{formatDuration(displayElapsedSeconds)}</div>
                </div>
              )}
              {isLoading && status.current_step && currentStepOverallProgress !== null && (
                <div style={styles.statItem}>
                  <div style={styles.statLabel}>Current Step</div>
                  <div style={styles.statValue}>
                    {stepLabels[status.current_step] || status.current_step}: {currentStepOverallProgress}%
                  </div>
                </div>
              )}
              {status.estimated_completion !== null && status.overall_status === 'loading' && (
                <div style={styles.statItem}>
                  <div style={styles.statLabel}>ETA</div>
                  <div style={styles.statValue}>{formatETA(displayEtaSeconds)}</div>
                </div>
              )}
            </div>
          </div>

          {/* Current Activity Section - shows what sync is doing right now */}
          {status.overall_status === 'loading' && (
            <div style={{
              padding: spacing.md,
              backgroundColor: `${colors.primary}10`,
              border: `1px solid ${colors.primary}`,
              borderRadius: '6px',
            }}>
              <div style={{
                fontSize: typographyFlat.fontSize.sm,
                fontWeight: 600,
                color: colors.primary,
                marginBottom: spacing.sm,
              }}>
                Current Activity
              </div>
              <div style={{
                fontSize: typographyFlat.fontSize.xs,
                color: colors.text,
                lineHeight: 1.6,
              }}>
                <div style={{ marginBottom: spacing.xs, fontWeight: 600 }}>
                  {status.current_activity || 'Starting sync workflow...'}
                </div>
                <div style={{ color: colors.textSecondary }}>
                  Last update: {formatActivityUpdatedAt(status.activity_updated_at)}
                </div>
                <div style={{ color: colors.textSecondary }}>
                  Heartbeat age: {formatHeartbeatAge(status.activity_updated_at)}
                </div>
                {status.last_log_line && (
                  <div style={{
                    marginTop: spacing.xs,
                    padding: `${spacing.xs} ${spacing.sm}`,
                    backgroundColor: colors.background,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '4px',
                    color: colors.textSecondary,
                    fontFamily: 'monospace',
                    fontSize: typographyFlat.fontSize.xs,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {status.last_log_line}
                  </div>
                )}
              </div>
            </div>
          )}

          {status.overall_status === 'loading' && status.continuity_snapshot && (
            <div style={styles.continuityBox}>
              <div style={styles.continuityHeader}>
                <span>Continuity Watch</span>
                <span>{formatActivityUpdatedAt(status.continuity_snapshot.captured_at)}</span>
              </div>
              <div style={styles.continuityRow}>
                <span>MTGJSON UUID Coverage: {status.continuity_snapshot.app.mtgjson_uuid_coverage_pct}%</span>
                <span>TCG Market Coverage: {status.continuity_snapshot.app.tcg_market_coverage_pct}%</span>
                <span>Preferred Price Coverage: {status.continuity_snapshot.app.preferred_price_coverage_pct}%</span>
                <span>Snapshots (24h): {status.continuity_snapshot.app.pricing_snapshots_last_24h.toLocaleString()}</span>
              </div>
              <div style={styles.continuityRow}>
                <span>MTGJSON DB: {status.continuity_snapshot.mtgjson.db_available ? 'available' : 'unavailable'}</span>
                <span>cards: {status.continuity_snapshot.mtgjson.cards_rows ?? 'n/a'}</span>
                <span>allidentifiers: {status.continuity_snapshot.mtgjson.allidentifiers_rows ?? 'n/a'}</span>
                <span>cardprices: {status.continuity_snapshot.mtgjson.cardprices_rows ?? 'n/a'}</span>
              </div>
              {!status.continuity_snapshot.mtgjson.db_available && status.continuity_snapshot.mtgjson.error && (
                <div style={{ ...styles.error, marginTop: spacing.xs }}>
                  MTGJSON continuity probe error: {status.continuity_snapshot.mtgjson.error}
                </div>
              )}
            </div>
          )}

          {status.overall_status === 'failed' && status.run_errors && status.run_errors.length > 0 && (
            <div style={styles.runErrorBox}>
              <div style={styles.runErrorTitle}>Run failed</div>
              {status.run_errors.map((err, idx) => (
                <div key={`run-error-${idx}`} style={styles.runErrorItem}>{err}</div>
              ))}
            </div>
          )}

          <div style={styles.stepsContainer}>
            {['sets', 'supplementary', 'pricing', 'decks'].map((stepKey) => {
              const step = status.steps[stepKey];
              if (!step) return null;

              const stageLabels: Record<string, string> = {
                metadata_schema: 'Schema Setup',
                metadata_sets: 'Sets Import',
                metadata_translations: 'Set Translations',
                metadata_cards: 'Cards Import',
                metadata_identifiers: 'Card Identifiers',
                metadata_foreign_data: 'Foreign Data',
                metadata_legalities: 'Legalities',
                metadata_rulings: 'Rulings',
                metadata_purchase_urls: 'Purchase URLs',
                metadata_tokens: 'Tokens',
                metadata_token_identifiers: 'Token Identifiers',
                supplementary_prepare: 'Preparation',
                supplementary_enrichment: 'Enrichment Import',
                supplementary_meta: 'Meta Tracking',
                pricing_prepare: 'Preparation',
                pricing_sync: 'Price Import',
                pricing_validation: 'Validation',
                decks_prepare: 'Preparation',
                decks_sync: 'Deck Import',
              };

              const phaseSequences: Record<string, string[]> = {
                sets: [
                  'metadata_schema',
                  'metadata_sets',
                  'metadata_translations',
                  'metadata_cards',
                  'metadata_identifiers',
                  'metadata_foreign_data',
                  'metadata_legalities',
                  'metadata_rulings',
                  'metadata_purchase_urls',
                  'metadata_tokens',
                  'metadata_token_identifiers',
                ],
                supplementary: [
                  'supplementary_prepare',
                  'supplementary_enrichment',
                  'supplementary_meta',
                ],
                pricing: [
                  'pricing_prepare',
                  'pricing_sync',
                  'pricing_validation',
                ],
                decks: [
                  'decks_prepare',
                  'decks_sync',
                ],
              };

              const stageKey = step.stage || step.metadata_stage;
              const stageLabel = stageKey
                ? (stageLabels[stageKey] || stageKey)
                : null;
              const phaseSequence = phaseSequences[stepKey] || [];
              const totalPhases = phaseSequence.length;
              const currentPhaseIndex = stageKey ? phaseSequence.indexOf(stageKey) : -1;
              const completedPhases = step.status === 'success'
                ? totalPhases
                : (currentPhaseIndex >= 0 ? currentPhaseIndex + 1 : 0);
              const phaseProgressPercent =
                typeof step.phase_progress_percent === 'number'
                  ? step.phase_progress_percent
                  : step.progress_percent;
              const overallProgressPercent =
                typeof step.overall_progress_percent === 'number'
                  ? step.overall_progress_percent
                  : step.progress_percent;

              // Show if step is actively processing
              const isActive = step.phase === 'importing' || step.phase === 'downloading';
              const isTimedOut = step.phase === 'timed_out';
              
              return (
                <div key={stepKey} style={styles.step}>
                  <div style={styles.stepHeader}>
                    <div style={styles.stepName}>
                      {isActive && (
                        <span style={{ 
                          display: 'inline-block', 
                          marginRight: spacing.xs,
                          color: colors.primary,
                          fontWeight: 700,
                        }}>⚙</span>
                      )}
                      {stepLabels[stepKey] || stepKey}
                      {stageLabel && (
                        <span style={{ marginLeft: spacing.xs, color: colors.textSecondary, fontWeight: 500 }}>
                          ({stageLabel})
                        </span>
                      )}
                      {totalPhases > 0 && (
                        <span style={{ marginLeft: spacing.xs, color: colors.textSecondary, fontWeight: 500 }}>
                          [{completedPhases}/{totalPhases} phases]
                        </span>
                      )}
                    </div>
                    <div 
                      style={{
                        ...styles.stepStatus,
                        backgroundColor: `${getStatusColor(step.status)}22`,
                        color: getStatusColor(step.status),
                        fontWeight: (isActive || isTimedOut) ? 700 : 500,
                      }}
                    >
                      {getStepPhaseLabel(step)}
                    </div>
                  </div>

                  <div style={styles.stepProgressBar}>
                    <div 
                      style={{
                        ...styles.stepProgressFill,
                        width: `${phaseProgressPercent}%`,
                        backgroundColor: getStatusColor(step.status),
                      }}
                    />
                  </div>

                  <div style={styles.stepDetails}>
                    {isActive && step.records_processed === 0 ? (
                      <span style={{ color: colors.primary, fontWeight: 600 }}>
                        {step.current_activity || 'Processing...'}
                      </span>
                    ) : (
                      <span>
                        Phase {phaseProgressPercent}%
                        {isLoading && status.current_step === stepKey && currentStepOverallProgress !== null
                          ? ` | Overall ${currentStepOverallProgress}%`
                          : ` | Overall ${overallProgressPercent}%`}
                      </span>
                    )}
                    <span>•</span>
                    <span>{step.records_processed.toLocaleString()} records</span>
                    {step.total_records > 0 && (
                      <>
                        <span>•</span>
                        <span>{step.total_records.toLocaleString()} total</span>
                      </>
                    )}
                    {step.errors > 0 && (
                      <>
                        <span>•</span>
                        <span style={{ color: colors.error }}>{step.errors} errors</span>
                      </>
                    )}
                    {step.timed_out_after_seconds && (
                      <>
                        <span>•</span>
                        <span style={{ color: colors.error }}>timeout {step.timed_out_after_seconds}s</span>
                      </>
                    )}
                  </div>
                  {step.activity_updated_at && (
                    <div style={styles.stepDetails}>
                      <span>Heartbeat {formatHeartbeatAge(step.activity_updated_at)}</span>
                      {step.last_log_line && (
                        <>
                          <span>•</span>
                          <span>{step.last_log_line}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
